<template>
    <div :class="[$style.wrapper, props.keepSpaceForInstreamInfo && $style.bottom_space]">
        <video
            ref="videoElem"
            :class="[$style.video, isVideoVisible && $style.visible]"
            playsinline
        />
    </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { onMounted, onUnmounted, ref } from 'vue'

import { AdLoader } from '@/modules/adv/ad-loader'
import { useAppStore } from '@/store/app-store'
import { random, timer } from '@/utils/helpers'

import { LOADER_FILLER_DURATION_MS } from '../../utils'

const videos = [
    {
        vertical: 'https://static.playgama.com/p-video/ad-free/bear_9x16_480x854.mp4',
        horizontal: 'https://static.playgama.com/p-video/ad-free/bear_16x9_960_540.mp4',
    },
    {
        vertical: 'https://static.playgama.com/p-video/ad-free/sport_480x854.mp4',
        horizontal: 'https://static.playgama.com/p-video/ad-free/sport_16x9_960_540.mp4',
    },
]

type DisabledAdFillerMessage = { type: 'stop' | 'timer'; secondsLeft?: number }

const emit = defineEmits<{
    message: [payload: DisabledAdFillerMessage]
}>()

const props = defineProps<{
    adLoader?: AdLoader
    keepSpaceForInstreamInfo?: boolean
}>()

const { platform } = storeToRefs(useAppStore())

const isVideoVisible = ref(false)
const videoElem = ref<HTMLVideoElement>()

const loaderTimer = ref<ReturnType<typeof setTimeout>>()
const removeListeners = ref<() => void>()

function loadVideo(videoEl: HTMLVideoElement): void {
    const video = videos[Math.floor(random() * videos.length)]
    const isHorizontal = window.innerHeight < window.innerWidth
    videoEl.src = platform.value === 'desktop' || isHorizontal ? video!.horizontal : video!.vertical
}

async function startVideo() {
    props.adLoader?.show('rewarded')
    const videoEl = videoElem.value
    if (!videoEl) {
        await props.adLoader?.destroy()
        return
    }
    loadVideo(videoEl)
    if (props.adLoader) {
        await timer(LOADER_FILLER_DURATION_MS)
    }

    try {
        await videoEl.play()
        if (props.adLoader) {
            props.adLoader?.destroy().then(() => {
                start(videoEl)
            })
        } else {
            start(videoEl)
        }
    } catch {
        await props.adLoader?.destroy()
        emit('message', { type: 'stop' })
        return
    }

    const onTimeUpdate = () => {
        const secondsLeft = Math.round(videoEl.duration - videoEl.currentTime)
        emit('message', { type: 'timer', secondsLeft })
    }

    const onEnded = () => {
        removeListeners.value?.()
        removeListeners.value = undefined
        isVideoVisible.value = false
        emit('message', { type: 'stop' })
    }

    removeListeners.value = () => {
        videoEl.removeEventListener('timeupdate', onTimeUpdate)
        videoEl.removeEventListener('ended', onEnded)
    }

    videoEl.addEventListener('timeupdate', onTimeUpdate)
    videoEl.addEventListener('ended', onEnded, { once: true })
}

function start(videoEl: HTMLVideoElement) {
    isVideoVisible.value = true
    videoEl.play()
}

onMounted(() => {
    startVideo()
})

onUnmounted(() => {
    clearTimeout(loaderTimer.value)
    removeListeners.value?.()
    props.adLoader?.destroy()
})
</script>

<style module>
.wrapper {
    background-color: rgba(0, 0, 0, 0.9);
    display: flex;
    width: 100%;
    position: fixed;
    inset: 0;
    z-index: 100;
}

.bottom_space {
    height: calc(100vh - 56px);
}

.preloader {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.video {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: transparent;
}
.visible {
    opacity: 1;
}
</style>
